import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { askConfirm } from './ask';

/**
 * Warns before leaving a form with changes that have not been saved: the
 * browser's own prompt for a reload, a closed tab or a typed address, and our
 * dialog for a link or Back inside the app. `when` should be false while a
 * save is in flight, so the save's own navigation is never stopped.
 */
export function UnsavedChanges(props: { when: boolean }) {
  useEffect(() => {
    if (!props.when) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [props.when]);

  // Blocking in-app navigation needs a data router; tests render under a plain one.
  return useContext(UNSAFE_DataRouterContext) ? <InAppGuard when={props.when} /> : null;
}

function InAppGuard(props: { when: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      props.when && currentLocation.pathname !== nextLocation.pathname,
  );
  const latest = useRef(blocker);
  latest.current = blocker;
  // Guards against a second dialog when an effect runs twice (StrictMode in development).
  const asking = useRef(false);

  useEffect(() => {
    if (blocker.state !== 'blocked' || asking.current) return;
    asking.current = true;
    void askConfirm({
      title: 'Leave without saving?',
      body: 'Your changes on this page have not been saved and will be lost.',
      confirmLabel: 'Leave page',
      danger: true,
    }).then((leave) => {
      asking.current = false;
      const b = latest.current;
      if (b.state !== 'blocked') return;
      if (leave) b.proceed();
      else b.reset();
    });
  }, [blocker.state]);

  return null;
}
